import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  buildWrongAnswerTableRows,
  getWrongAnswersForReport,
  WRONG_ANSWERS_EMPTY_MESSAGE,
} from './quizReport';

/** Matches the live portal: light surfaces, charcoal type, Apple blue. */
const C = {
  ink: [29, 29, 31],
  muted: [110, 110, 115],
  blue: [0, 113, 227],
  red: [185, 48, 40],
  green: [36, 128, 70],
  line: [229, 229, 234],
  surface: [245, 245, 247],
  stripe: [245, 248, 252],
  header: [255, 255, 255],
  white: [255, 255, 255],
};

const LOGO_URL = '/diologic-logo.png';
const LOGO_ASPECT = 2135 / 800;
const MARGIN = 16;
const HEADER_H = 28;
const LOGO_H = 18;
const LOGO_W = LOGO_H * LOGO_ASPECT;
const ACCENT_H = 1.2;
const FOOTER_H = 16;
const CONTENT_TOP = HEADER_H + ACCENT_H + 10;
const PAGE_W = 210;
const CONTENT_W = PAGE_W - MARGIN * 2;

const WRONG_ANSWER_TABLE_HEAD = [['Question No.', 'Question', 'Selected answer', 'Correct answer']];

let logoDataUrlCache = null;
let logoLoadPromise = null;

const loadLogoDataUrl = () => {
  if (logoDataUrlCache) return Promise.resolve(logoDataUrlCache);
  if (logoLoadPromise) return logoLoadPromise;

  logoLoadPromise = fetch(LOGO_URL)
    .then((res) => {
      if (!res.ok) throw new Error('Logo fetch failed');
      return res.blob();
    })
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            logoDataUrlCache = reader.result;
            resolve(logoDataUrlCache);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
    )
    .catch(() => {
      logoDataUrlCache = null;
      return null;
    });

  return logoLoadPromise;
};

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'number' || typeof value === 'string') return new Date(value);
  return null;
};

const pct = (score, total) =>
  total && total > 0 ? Math.round(((score || 0) / total) * 100) : 0;

const safe = (s, fallback = 'report') =>
  String(s || fallback).replace(/[^a-zA-Z0-9.-]/g, '-').slice(0, 60);

const formatDateTime = (value) => {
  const d = value instanceof Date ? value : toDate(value);
  if (!d || Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
};

const formatDate = (value) => {
  const d = value instanceof Date ? value : toDate(value) || new Date();
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(d);
};

const applyDocumentChrome = (doc, logoDataUrl, { documentTitle, issued }) => {
  const pageCount = doc.getNumberOfPages();
  const pageH = doc.internal.pageSize.getHeight();
  const logoY = (HEADER_H - LOGO_H) / 2;
  const issuedLabel = issued || formatDate(new Date());
  const reportLabel = documentTitle || 'Assessment report';

  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);

    doc.setFillColor(...C.header);
    doc.rect(0, 0, PAGE_W, HEADER_H, 'F');
    doc.setFillColor(...C.blue);
    doc.rect(0, HEADER_H, PAGE_W, ACCENT_H, 'F');

    let logoDrawn = false;
    if (logoDataUrl) {
      try {
        doc.addImage(logoDataUrl, 'PNG', MARGIN, logoY, LOGO_W, LOGO_H, undefined, 'FAST');
        logoDrawn = true;
      } catch {
        logoDrawn = false;
      }
    }
    if (!logoDrawn) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(...C.ink);
      doc.text('Diologic', MARGIN, HEADER_H / 2 + 2);
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...C.ink);
    doc.text(reportLabel, PAGE_W - MARGIN, 12, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...C.muted);
    doc.text(issuedLabel, PAGE_W - MARGIN, 19.5, { align: 'right' });

    doc.setDrawColor(...C.line);
    doc.setLineWidth(0.25);
    doc.line(MARGIN, pageH - FOOTER_H, PAGE_W - MARGIN, pageH - FOOTER_H);
    doc.setFontSize(8);
    doc.setTextColor(...C.muted);
    doc.text('Diologic  ·  Assessment report', MARGIN, pageH - 8);
    doc.text(`${i}  /  ${pageCount}`, PAGE_W - MARGIN, pageH - 8, { align: 'right' });
  }
};

const finalizePdf = async (doc, filename, chrome) => {
  const logo = await loadLogoDataUrl();
  applyDocumentChrome(doc, logo, chrome);
  doc.save(filename);
};

const drawPageTitle = (doc, title, kicker) => {
  let y = CONTENT_TOP;
  if (kicker) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...C.blue);
    doc.text(String(kicker).toUpperCase(), MARGIN, y);
    y += 7;
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...C.ink);
  const lines = doc.splitTextToSize(String(title || 'Quiz'), CONTENT_W);
  doc.text(lines, MARGIN, y);
  return y + lines.length * 6.5 + 4;
};

const drawMetricCards = (doc, y, cards) => {
  const gap = 3.5;
  const n = cards.length;
  const cardW = (CONTENT_W - gap * (n - 1)) / n;
  const h = 22;

  cards.forEach((card, i) => {
    const x = MARGIN + i * (cardW + gap);
    doc.setFillColor(...C.surface);
    doc.roundedRect(x, y, cardW, h, 2, 2, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...C.muted);
    doc.text(String(card.label).toUpperCase(), x + 4, y + 7);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...(card.color || C.ink));
    const valueLines = doc.splitTextToSize(String(card.value), cardW - 8);
    doc.text(valueLines[0], x + 4, y + 16.5);
  });

  return y + h + 6;
};

const drawMetaCard = (doc, y, pairs) => {
  const rowH = 7;
  const h = 8 + pairs.length * rowH;
  doc.setFillColor(...C.surface);
  doc.roundedRect(MARGIN, y, CONTENT_W, h, 2, 2, 'F');

  pairs.forEach((pair, i) => {
    const rowY = y + 7 + i * rowH;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...C.muted);
    doc.text(pair.label, MARGIN + 4, rowY);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...C.ink);
    const value = doc.splitTextToSize(String(pair.value || '—'), CONTENT_W - 52);
    doc.text(value[0], MARGIN + 42, rowY);
  });

  return y + h + 8;
};

const drawSectionLabel = (doc, y, label) => {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...C.muted);
  const lines = doc.splitTextToSize(String(label).toUpperCase(), CONTENT_W);
  doc.text(lines, MARGIN, y);
  const underlineY = y + 2.6 + (lines.length - 1) * 3.5;
  doc.setFillColor(...C.blue);
  doc.rect(MARGIN, underlineY, 14, 0.65, 'F');
  return y + 8 + (lines.length - 1) * 3.5;
};

const tableBaseStyles = (fontSize = 8.5) => ({
  theme: 'plain',
  margin: { left: MARGIN, right: MARGIN, top: CONTENT_TOP, bottom: FOOTER_H + 4 },
  tableWidth: CONTENT_W,
  styles: {
    font: 'helvetica',
    fontSize,
    cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
    overflow: 'linebreak',
    valign: 'top',
    minCellHeight: 9,
    textColor: C.ink,
    lineColor: C.line,
    lineWidth: 0.15,
  },
  headStyles: {
    fillColor: C.surface,
    textColor: C.ink,
    fontStyle: 'bold',
    fontSize: 7.5,
    valign: 'middle',
    cellPadding: { top: 3.5, right: 3, bottom: 3.5, left: 3 },
    overflow: 'linebreak',
  },
  alternateRowStyles: { fillColor: C.stripe },
  rowPageBreak: 'auto',
  showHead: 'everyPage',
});

const wrongAnswerTableOptions = (startY, fontSize = 8.5) => ({
  ...tableBaseStyles(fontSize),
  head: WRONG_ANSWER_TABLE_HEAD,
  startY,
  columnStyles: {
    0: { cellWidth: 22, overflow: 'linebreak', fontStyle: 'bold', halign: 'center' },
    1: { cellWidth: 76, overflow: 'linebreak' },
    2: { cellWidth: 40, overflow: 'linebreak', textColor: C.red },
    3: { cellWidth: 40, overflow: 'linebreak', textColor: C.green },
  },
  didParseCell: (data) => {
    if (data.section !== 'body') return;
    if (data.column.index === 2) data.cell.styles.textColor = C.red;
    if (data.column.index === 3) data.cell.styles.textColor = C.green;
  },
});

const drawWrongAnswersTable = (doc, answers, startY, fontSize = 8.5) => {
  autoTable(doc, {
    ...wrongAnswerTableOptions(startY, fontSize),
    body: buildWrongAnswerTableRows(answers),
  });
};

const drawEmptyNotice = (doc, y, message) => {
  doc.setFillColor(...C.surface);
  doc.roundedRect(MARGIN, y, CONTENT_W, 16, 2, 2, 'F');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...C.muted);
  doc.text(message, MARGIN + 5, y + 10);
  return y + 20;
};

const parseCompletedDate = (completedAt) => {
  if (completedAt instanceof Date) return completedAt;
  if (typeof completedAt === 'number' || typeof completedAt === 'string') return new Date(completedAt);
  const d = toDate(completedAt);
  return d || new Date();
};

/**
 * Generate a PDF report for a single quiz attempt.
 */
export const generateAttemptPdf = async ({
  player,
  userEmail,
  sessionTitle,
  score,
  totalQuestions,
  answers = [],
  completedAt,
}) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const title = sessionTitle || 'Quiz';
  const nameOrEmail =
    player?.displayName ||
    player?.email ||
    userEmail ||
    'Unknown user';
  const completedDate = parseCompletedDate(completedAt);
  const percentage = pct(score, totalQuestions);
  const wrongAnswers = getWrongAnswersForReport(answers);

  let y = drawPageTitle(doc, title, 'Quiz report');
  y = drawMetricCards(doc, y, [
    { label: 'Score', value: `${score ?? 0} / ${totalQuestions ?? 0}  (${percentage}%)` },
    {
      label: 'Incorrect',
      value: String(wrongAnswers.length),
      color: wrongAnswers.length ? C.red : C.green,
    },
    { label: 'Completed', value: formatDate(completedDate) },
  ]);
  y = drawMetaCard(doc, y, [
    { label: 'Candidate', value: nameOrEmail },
    { label: 'Email', value: userEmail || player?.email || '—' },
    { label: 'Completed', value: formatDateTime(completedDate) },
  ]);
  y = drawSectionLabel(doc, y, 'Incorrect answers');

  if (wrongAnswers.length === 0) {
    drawEmptyNotice(doc, y, WRONG_ANSWERS_EMPTY_MESSAGE);
  } else {
    drawWrongAnswersTable(doc, answers, y);
  }

  const filename = `QuizReport-${safe(title, 'quiz')}-${safe(userEmail || player?.email, 'user')}.pdf`;
  await finalizePdf(doc, filename, {
    documentTitle: 'Quiz report',
    issued: formatDate(completedDate),
  });
};

/**
 * Individual consultant report — all quiz attempts for a single consultant.
 */
export const generateConsultantReportPdf = async ({ userEmail, playerName, completions = [] }) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const name = (playerName || '').trim() || userEmail || 'Consultant';
  const issued = formatDate(new Date());

  const attempts = [...completions].sort((a, b) => {
    const ta = toDate(a.completedAt)?.getTime() ?? 0;
    const tb = toDate(b.completedAt)?.getTime() ?? 0;
    return tb - ta;
  });

  const percentages = attempts.map((c) => pct(c.score, c.totalQuestions));
  const avg = percentages.length
    ? Math.round(percentages.reduce((s, p) => s + p, 0) / percentages.length)
    : 0;
  const best = percentages.length ? Math.max(...percentages) : 0;

  let y = drawPageTitle(doc, name, 'Individual consultant report');
  y = drawMetricCards(doc, y, [
    { label: 'Attempts', value: String(attempts.length) },
    { label: 'Average', value: `${avg}%` },
    { label: 'Best', value: `${best}%` },
  ]);
  y = drawMetaCard(doc, y, [{ label: 'Email', value: userEmail || '—' }]);
  y = drawSectionLabel(doc, y, 'Attempt summary');

  autoTable(doc, {
    ...tableBaseStyles(8.5),
    startY: y,
    head: [['#', 'Quiz / session', 'Score', '%', 'Completed']],
    body: attempts.map((c, i) => [
      String(i + 1),
      String(c.sessionTitle || 'Quiz'),
      `${c.score ?? 0} / ${c.totalQuestions ?? 0}`,
      `${pct(c.score, c.totalQuestions)}%`,
      formatDateTime(c.completedAt),
    ]),
    columnStyles: {
      0: { cellWidth: 12, halign: 'center', fontStyle: 'bold' },
      1: { cellWidth: 78 },
      2: { cellWidth: 28, halign: 'center' },
      3: { cellWidth: 18, halign: 'center' },
      4: { cellWidth: 42 },
    },
  });

  const pageHeight = doc.internal.pageSize.getHeight();
  attempts.forEach((c, i) => {
    const answers = c.answers || [];
    const wrong = getWrongAnswersForReport(answers);
    let cursorY = (doc.lastAutoTable?.finalY || y) + 10;
    if (cursorY > pageHeight - 50) {
      doc.addPage();
      cursorY = CONTENT_TOP;
    }

    cursorY = drawSectionLabel(
      doc,
      cursorY,
      `${i + 1}.  ${c.sessionTitle || 'Quiz'}   ·   ${c.score ?? 0}/${c.totalQuestions ?? 0} (${pct(c.score, c.totalQuestions)}%)`
    );
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...C.muted);
    const attemptDate = formatDateTime(c.completedAt);
    if (attemptDate !== '—') {
      doc.text(`Completed  ${attemptDate}`, MARGIN, cursorY - 2);
      cursorY += 4;
    }

    if (answers.length === 0) {
      drawEmptyNotice(doc, cursorY, 'No per-question detail available for this attempt.');
      return;
    }
    if (wrong.length === 0) {
      drawEmptyNotice(doc, cursorY, WRONG_ANSWERS_EMPTY_MESSAGE);
      return;
    }

    doc.setFontSize(8);
    doc.setTextColor(...C.red);
    doc.text(`Incorrect answers  ·  ${wrong.length}`, MARGIN, cursorY + 2);
    doc.setTextColor(...C.ink);
    drawWrongAnswersTable(doc, answers, cursorY + 6, 8);
  });

  await finalizePdf(doc, `ConsultantReport-${safe(userEmail, 'consultant')}.pdf`, {
    documentTitle: 'Individual consultant report',
    issued,
  });
};

/**
 * Total consultants report — one summary row per consultant across all completions.
 */
export const generateAllConsultantsReportPdf = async ({ completions = [] }) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const issued = formatDate(new Date());

  const byConsultant = new Map();
  completions.forEach((c) => {
    const key = (c.userEmail || 'unknown').toLowerCase();
    if (!byConsultant.has(key)) {
      byConsultant.set(key, {
        email: c.userEmail || 'unknown',
        name: (c.playerName || '').trim(),
        percentages: [],
        lastAttempt: null,
      });
    }
    const entry = byConsultant.get(key);
    if (!entry.name && (c.playerName || '').trim()) entry.name = c.playerName.trim();
    entry.percentages.push(pct(c.score, c.totalQuestions));
    const d = toDate(c.completedAt);
    if (d && (!entry.lastAttempt || d > entry.lastAttempt)) entry.lastAttempt = d;
  });

  const rows = Array.from(byConsultant.values())
    .map((e) => {
      const attempts = e.percentages.length;
      const avg = attempts ? Math.round(e.percentages.reduce((s, p) => s + p, 0) / attempts) : 0;
      const best = attempts ? Math.max(...e.percentages) : 0;
      return { ...e, attempts, avg, best };
    })
    .sort((a, b) => b.avg - a.avg);

  const overallAvg = rows.length
    ? Math.round(rows.reduce((s, r) => s + r.avg, 0) / rows.length)
    : 0;

  let y = drawPageTitle(doc, 'Consultants summary', 'Organisation report');
  y = drawMetricCards(doc, y, [
    { label: 'Consultants', value: String(rows.length) },
    { label: 'Attempts', value: String(completions.length) },
    { label: 'Overall average', value: `${overallAvg}%` },
  ]);
  y = drawSectionLabel(doc, y, 'Performance');

  autoTable(doc, {
    ...tableBaseStyles(8.5),
    startY: y,
    head: [['Consultant', 'Email', 'Attempts', 'Avg %', 'Best %', 'Last attempt']],
    body: rows.map((r) => [
      r.name || '—',
      r.email,
      String(r.attempts),
      `${r.avg}%`,
      `${r.best}%`,
      r.lastAttempt ? formatDate(r.lastAttempt) : '—',
    ]),
    columnStyles: {
      0: { cellWidth: 36 },
      1: { cellWidth: 52 },
      2: { cellWidth: 22, halign: 'center' },
      3: { cellWidth: 20, halign: 'center' },
      4: { cellWidth: 20, halign: 'center' },
      5: { cellWidth: 28 },
    },
  });

  await finalizePdf(doc, `AllConsultantsReport-${new Date().toISOString().slice(0, 10)}.pdf`, {
    documentTitle: 'Consultants summary',
    issued,
  });
};
